import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, ShieldCheck, Heart, ChevronLeft } from "lucide-react";
import api, { money } from "@/lib/api";
import { StarRating, Label, FreePill } from "@/components/Badges";
import InventoryMeter from "@/components/InventoryMeter";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [fav, setFav] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/products/${slug}`).then((r) => { setP(r.data); setImg(0); }); }, [slug]);

  const toggleFav = async () => {
    if (!user) { toast.info("Sign in to save Friibs"); return; }
    try { const { data } = await api.post(`/me/favorite/${slug}`); setFav(data.favorites.includes(slug)); toast.success(fav ? "Removed" : "Saved to favorites"); } catch {}
  };

  if (!p) return <div className="min-h-[60vh] grid place-items-center text-stone-400">Loading…</div>;

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-stone-900"><ChevronLeft size={16} /> Back to shop</Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-2 gap-10">
        {/* GALLERY */}
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-white friib-shadow border border-stone-100">
            <img src={p.images[img]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((im, i) => (
                <button key={i} onClick={() => setImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === img ? "border-[var(--friib)]" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div>
          <div className="flex items-center justify-between">
            <Link to={`/shop?brand=${p.brand}`} className="text-sm font-bold uppercase tracking-wider" style={{ color: p.brand_color }}>{p.brand_name}</Link>
            <button onClick={toggleFav} data-testid="favorite-btn" className="w-10 h-10 grid place-items-center rounded-full bg-stone-100 hover:bg-stone-200">
              <Heart size={18} className={fav ? "fill-red-500 text-red-500" : "text-stone-600"} />
            </button>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-stone-900 mt-2 leading-tight">{p.name}</h1>
          <div className="mt-3 flex items-center gap-3">
            <StarRating rating={p.rating} count={p.review_count} />
            <div className="flex gap-1.5">{(p.labels || []).map((l) => <Label key={l}>{l}</Label>)}</div>
          </div>

          <div className="mt-6 rounded-2xl bg-white border border-stone-100 friib-shadow p-6">
            <div className="flex items-end gap-3 mb-4">
              <span className="text-sm text-stone-400 line-through">Retail value {money(p.retail_value)}</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display text-5xl font-extrabold" style={{ color: "var(--mint)" }}>$0</span>
              <FreePill className="text-base px-4 py-1.5" />
            </div>
            <p className="flex items-center gap-2 text-stone-700 font-semibold mb-5"><Truck size={18} style={{ color: "var(--friib)" }} /> Shipping & handling: {money(p.shipping)}</p>
            <InventoryMeter claimed={p.inventory_claimed} total={p.inventory_total} remaining={p.inventory_remaining} className="mb-5" />
            <button onClick={() => nav(`/checkout/${p.slug}`)} data-testid="claim-friib-button"
              disabled={p.inventory_remaining <= 0}
              className="w-full py-4 rounded-full font-extrabold text-white text-lg transition-transform hover:scale-[1.01] disabled:opacity-50" style={{ background: "var(--friib)" }}>
              {p.inventory_remaining <= 0 ? "Sold Out" : "Claim This Friib"}
            </button>
            <p className="mt-3 text-xs text-stone-500 flex items-center gap-1.5 justify-center"><ShieldCheck size={14} /> The brand provides the product for free. You only pay shipping & handling.</p>
          </div>

          <div className="mt-8 space-y-6">
            <Section title="Description">{p.description}</Section>
            <Section title="What's included">{p.whats_included}</Section>
            {p.ingredients && <Section title="Ingredients / specs">{p.ingredients}</Section>}
            {p.how_to_use && <Section title="How to use">{p.how_to_use}</Section>}
            {p.brand_info && <Section title="Brand story">{p.brand_info.description}</Section>}
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">Reviews ({p.reviews.length})</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {p.reviews.map((r, i) => (
            <div key={i} className="rounded-2xl bg-white border border-stone-100 p-5 friib-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-stone-900">{r.user_name}</span>
                <StarRating rating={r.rating} size={13} />
              </div>
              <p className="text-stone-600">{r.text}</p>
              {r.photo && <img src={r.photo} alt="" className="mt-3 w-24 h-24 object-cover rounded-xl" />}
            </div>
          ))}
        </div>
      </div>

      {/* RELATED */}
      {p.related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">Related Friibs</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">{p.related.map((r, i) => <ProductCard key={r.slug} p={r} index={i} />)}</div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-stone-200 pb-5">
      <h3 className="font-display font-bold text-stone-900 mb-1.5">{title}</h3>
      <p className="text-stone-600 leading-relaxed">{children}</p>
    </div>
  );
}
